import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Grid,
  Typography,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { ExpandMoreIcon } from 'components/common/Icon';
import { useCurrentAccount } from 'hooks/api/useAccounts';
import {
  useCurrentCompany,
  useUpdateCompanyProgress,
} from 'hooks/api/useCompanies';
import React, { useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from 'theme';
import {
  IAccount,
  ProgressTrackerGroupRequest,
  ProgressTrackerGroups,
  ProgressTrackerStages,
  ProgressTrackerStatus,
} from 'services/account';
import { Business } from 'models/business';
import { initialStateBIB, reducer } from './state';
import { AccountVerificationLayout } from './AccountVerificationLayout';
import { StageSkeleton } from '../StageSkeleton';
import { AccordionCTA } from '../AccordionCTA';
import { StageStatusColumn, StageStatusText } from '../Components/StageStatusColumn';

interface AccountVerificationComponentProps {
  businessData: Business;
  account: IAccount;
  expanded: boolean | string;
  handleExpand: (
    panel: string,
  ) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => void;
  onContinueToNextSection: () => void;
}

const AccountVerificationBIB = ({
  businessData,
  account: accData,
  expanded,
  handleExpand,
  onContinueToNextSection,
}: AccountVerificationComponentProps) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, initialStateBIB());
  /**
   *
   * Logic:
   *
   * - Handling the active step based on the response of the api.
   * - After every update, we need to call updated progress api to update the status.
   * - On success of update progress, we will invalidate the get current account api.
   *   We need to have latest status changes. Which will inturn set the active step.
   *
   */

  const [incorporationActiveStep, setIncorporationActiveStep] = useState(0);
  const [completedStep, setCompletedStep] = useState(0);

  const makeIdentityVerificationStepsAppearCompleted = (
    stage: ProgressTrackerStages,
    status: ProgressTrackerStatus,
  ) => {
    if (
      (stage === ProgressTrackerStages.UploadIdentityProof ||
        stage === ProgressTrackerStages.UploadLastYearTaxStatement) &&
      status === ProgressTrackerStatus.InProgress
    ) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    /**
     * Only update and show correct step only when we land first time or do refresh.
     *
     * On update, the onSuccess callback will handle updation of the step.
     */

    const states = Object.keys(state);

    if (states.length > 0) {
      let completedCount: number = 0;
      states.forEach((item) => {
        if (
          state[item]?.status === ProgressTrackerStatus.Completed ||
          makeIdentityVerificationStepsAppearCompleted(
            state[item]?.data?.stage,
            state[item]?.status,
          )
        ) {
          completedCount += 1;
        }
      });
      setCompletedStep(completedCount);
    }
  }, [state]);

  const { updateCompanyProgressAsync, isLoading: IsUpdatingProgress } =
    useUpdateCompanyProgress(businessData?.id);

  useEffect(() => {
    if (businessData?.progress?.length > 0) {
      dispatch({
        type: ProgressTrackerStages.UploadIdentityProof,
        value: businessData?.progress,
      });
      dispatch({
        type: ProgressTrackerStages.UploadLastYearTaxStatement,
        value: businessData?.progress,
      });
      dispatch({
        type: ProgressTrackerStages.HomeAddressDetails,
        value: businessData?.progress,
      });
    }
  }, [businessData]);

  const onContinue = async (
    stage: ProgressTrackerStages,
    status: ProgressTrackerStatus,
  ) => {
    const requestProps: ProgressTrackerGroupRequest = {
      group: ProgressTrackerGroups.AccountVerification,
      stage,
      status,
    };

    await updateCompanyProgressAsync({
      progress: [requestProps],
      eventData: {
        stage,
        entityType: businessData?.entityType ?? '',
      },
    });
  };

  const steps = Object.keys(state);
  const stageCompleted = completedStep === steps.length;

  return (
    <Accordion
      expanded={expanded === 'account-verification'}
      onChange={handleExpand('account-verification')}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="account-verification-content"
        id="account-verification-header"
        sx={{
          alignItems: 'flex-start',
        }}
      >
        <Grid container>
          <Grid item xs={10}>
            <Typography variant="h6B">Verify Your Account</Typography>
            <Typography variant="subtitle1">
              {t(
                'progressTracker.accountVerification.accountVerificationSubTitle',
              )}
            </Typography>
            <Typography variant="body2" color={theme.palette.text.secondary}>
              {t('progressTracker.accountVerification.processingTime')}
            </Typography>
            <AccordionCTA
              expanded={expanded}
              handleExpand={handleExpand}
              completed={completedStep}
              totalStep={steps.length}
              testId="start-account-verification"
              accordionId="account-verification"
            />
          </Grid>
          <StageStatusColumn item xs={2}>
            <StageStatusText variant="h8B">
              {stageCompleted && <CheckCircle color="secondary" />}
              {completedStep}/{steps.length}
            </StageStatusText>
          </StageStatusColumn>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <AccountVerificationLayout
          activeStep={incorporationActiveStep}
          setActiveStep={setIncorporationActiveStep}
          handleContinue={onContinue}
          isLoading={IsUpdatingProgress}
          state={state}
          entityType={businessData?.entityType ?? ''}
          account={accData}
          business={businessData}
          onContinueToNextSection={onContinueToNextSection}
        />
      </AccordionDetails>
    </Accordion>
  );
};

interface AccountVerificationComponentBIBProps {
  expanded: boolean | string;
  handleExpand: (
    panel: string,
  ) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => void;
  onContinueToNextSection: () => void;
}

export const AccountVerificationComponentBIB: React.FC<
  AccountVerificationComponentBIBProps
> = ({ expanded, handleExpand, onContinueToNextSection }) => {
  const {
    currentCompany: businessData,
    isLoading: companyDataLoading,
    isError: isCompanyDataError,
  } = useCurrentCompany();
  const {
    currentAccount,
    isLoading: fetchingCurrentAccount,
    isError: isAccountError,
  } = useCurrentAccount();

  if (fetchingCurrentAccount || companyDataLoading) {
    return <StageSkeleton />;
  }
  if (businessData != null && currentAccount != null) {
    return (
      <AccountVerificationBIB
        businessData={businessData}
        account={currentAccount}
        handleExpand={handleExpand}
        expanded={expanded}
        onContinueToNextSection={onContinueToNextSection}
      />
    );
  }
  if (isCompanyDataError && isAccountError) {
    return (
      <Alert severity="error">
        Error fetching data. Please try to refresh or contact for support
      </Alert>
    );
  }
  return null;
};
